import { ESTABELECIMENTO_FALLBACK_BASE64 } from '@/assets/fallback/estabBase64';
import { GenericSelect } from '@/components/GenericSelect';
import { useHasScopes } from '@/hooks/useHasScopes';
import { trpc, trpcClient } from '@/trpcClient';
import { logger } from '@/utils/logger';
import { checkFacilityCreateInput, type CreateFacilityInputType } from '@shelflix/schemas/facility/create';
import type { UserDto } from '@shelflix/trpc/routes/user/list/useCase';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent } from '@shelflix/ui-web/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shelflix/ui-web/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

interface CreateFacilityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFacilityCreated?: (facility: {
    uuid: string;
    name: string;
    cnpj: string;
    city: string;
    state: string;
    cityIbgeCode: string;
  }) => void;
}

interface CreateFacilityFormData extends CreateFacilityInputType {
  responsibleUserObject?: UserDto | null;
  // New user fields
  newUserName?: string;
  newUserRegistration?: string;
  newUserPhone?: string;
  newUserEmail?: string;
  newUserSupervisor?: UserDto | null;
}

/**
 * Convert a File to base64 string (without data URL prefix)
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function CreateFacilityModal({ open, onOpenChange, onFacilityCreated }: CreateFacilityModalProps) {
  const [isCreatingNewUser, setIsCreatingNewUser] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Permission checks
  const canCreateFacility = useHasScopes(['admin:facilities:create']);
  const canCreateUser = useHasScopes(['admin:users:create']);

  // Mutations
  const createFacilityMutation = useMutation(trpc.facility.create.mutationOptions());
  const createUserMutation = useMutation(trpc.user.create.mutationOptions());
  const uploadImageMutation = useMutation(trpc.image.uploadCompressed.mutationOptions());

  // Fetch lowest hierarchy role (Promotor) for creating new users
  const { data: lowestRole } = useQuery({
    ...trpc.role.getLowest.queryOptions(),
    enabled: canCreateUser && isCreatingNewUser,
  });

  // Fetch supervisors (users who can be responsible for the new promotor user)
  // Pre-load 50 items when the new user section is opened
  const { data: supervisorsData } = useQuery({
    ...trpc.user.list.queryOptions({
      filter: [
        { name: 'active', condition: 'equals', value: true },
        // Users with hierarchy less than promotor (supervisors)
        { name: 'hierarchy', condition: 'min', value: undefined },
      ],
      recordsPerPage: 50,
    }),
    enabled: canCreateUser && isCreatingNewUser,
  });

  const supervisorsList = supervisorsData?.users ?? [];

  const form = useForm<CreateFacilityFormData>({
    defaultValues: {
      name: '',
      cnpj: '',
      cep: '',
      address: '',
      district: '',
      number: '',
      city: '',
      state: '',
      cityIbgeCode: '',
      complement: '',
      referenceLocation: '',
      responsibleUser: '',
      responsibleUserObject: null,
      newUserName: '',
      newUserRegistration: '',
      newUserPhone: '',
      newUserEmail: '',
      newUserSupervisor: null,
    },
    mode: 'onSubmit',
  });

  // CEP auto-fill
  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (name !== 'cep') return;

      const cep = values.cep?.replace(/\D/g, '');

      if (cep?.length === 8) {
        const fetchAddress = async () => {
          try {
            const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await response.json();

            if (data.erro) {
              form.setError('cep', { message: 'CEP inválido ou não encontrado' });
              return;
            }

            form.clearErrors('cep');
            form.setValue('address', data.logradouro ?? '');
            form.setValue('district', data.bairro ?? '');
            form.setValue('city', data.localidade ?? '');
            form.setValue('state', data.uf ?? '');
            form.setValue('cityIbgeCode', data.ibge ?? '');
          } catch (_err) {
            form.setError('cep', { message: 'Erro ao buscar o endereço' });
          }
        };

        fetchAddress();
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      form.reset();
      setIsCreatingNewUser(false);
    }
  }, [open, form]);

  // Fetch users for responsible user selection
  const fetchUsers = useCallback(async (searchValue: string) => {
    try {
      const queryParams: {
        filter: { name: string; condition: string; value: unknown }[];
        search?: { fields: string[]; value: string };
        recordsPerPage?: number;
      } = {
        filter: [
          { name: 'active', condition: 'equals', value: true },
          { name: 'hierarchy', condition: 'min', value: undefined },
        ],
        recordsPerPage: 50,
      };

      // Only add search if there's a value
      if (searchValue && searchValue.trim().length > 0) {
        queryParams.search = {
          fields: ['name', 'email'],
          value: searchValue,
        };
      }

      const result = await trpcClient.user.list.query(queryParams);
      return result.users ?? [];
    } catch (error) {
      logger.error('Error fetching users:', error);
      return [];
    }
  }, []);

  // Convert fallback image to File
  const convertFallbackToFile = async (): Promise<File> => {
    const response = await fetch(ESTABELECIMENTO_FALLBACK_BASE64);
    const blob = await response.blob();
    return new File([blob], 'estabelecimento-fallback.jpeg', { type: 'image/jpeg' });
  };

  const handleToggleNewUser = () => {
    if (isCreatingNewUser) {
      // Cancel new user creation
      setIsCreatingNewUser(false);
      form.setValue('newUserName', '');
      form.setValue('newUserRegistration', '');
      form.setValue('newUserPhone', '');
      form.setValue('newUserEmail', '');
      form.setValue('newUserSupervisor', null);
    } else {
      // Start new user creation
      setIsCreatingNewUser(true);
      form.setValue('responsibleUser', '');
      form.setValue('responsibleUserObject', null);
    }
  };

  const onSubmit = async (data: CreateFacilityFormData) => {
    if (isSubmitting) return;

    // Validate permissions
    if (!canCreateFacility) {
      toast.error('Você não tem permissão para criar estabelecimentos');
      return;
    }

    if (isCreatingNewUser && !canCreateUser) {
      toast.error('Você não tem permissão para criar usuários');
      return;
    }

    setIsSubmitting(true);

    try {
      let responsibleUserUuid = data.responsibleUser;

      // Step 1: Create new user if needed
      if (isCreatingNewUser) {
        if (!lowestRole) {
          toast.error('Não foi possível obter o cargo de promotor');
          setIsSubmitting(false);
          return;
        }

        // Validate new user fields
        const hasPhone = data.newUserPhone && data.newUserPhone.replaceAll(/[^\d]/g, '').length >= 10;
        const hasEmail = data.newUserEmail && data.newUserEmail.trim().length > 0;
        const hasRegistration = data.newUserRegistration && data.newUserRegistration.trim().length > 0;

        if (!hasPhone && !hasEmail && !hasRegistration) {
          toast.error('É obrigatório informar pelo menos um dos campos: matrícula, telefone ou e-mail');
          setIsSubmitting(false);
          return;
        }

        if (!data.newUserName || data.newUserName.trim().length === 0) {
          toast.error('Nome do usuário é obrigatório');
          setIsSubmitting(false);
          return;
        }

        if (!data.newUserSupervisor) {
          toast.error('Responsável direto é obrigatório');
          setIsSubmitting(false);
          return;
        }

        const userResult = await createUserMutation.mutateAsync({
          name: data.newUserName.trim(),
          phone: data.newUserPhone?.replaceAll(/[^\d]/g, '') || undefined,
          email: data.newUserEmail?.trim() || undefined,
          registration: data.newUserRegistration?.trim() || undefined,
          role: lowestRole.uuid,
          responsibleUser: data.newUserSupervisor.uuid,
        });

        responsibleUserUuid = userResult.uuid;
        logger.info('User created successfully:', userResult.uuid);
      }

      // Step 2: Upload facility image
      const fileToUpload = await convertFallbackToFile();
      const base64Data = await fileToBase64(fileToUpload);
      const uploadResult = await uploadImageMutation.mutateAsync({
        base64Data,
        filename: fileToUpload.name,
        imageType: 'facility-logo',
      });
      const imageKey = uploadResult.key;
      logger.info('Image uploaded:', {
        originalSize: uploadResult.originalSize,
        compressedSize: uploadResult.compressedSize,
        savingsPercent: uploadResult.savingsPercent,
      });

      // Step 3: Create facility
      const facilityPayload: CreateFacilityInputType = {
        name: data.name,
        cnpj: data.cnpj,
        cep: data.cep,
        address: data.address,
        district: data.district,
        number: data.number,
        city: data.city,
        state: data.state,
        cityIbgeCode: data.cityIbgeCode,
        complement: data.complement,
        referenceLocation: data.referenceLocation,
        responsibleUser: responsibleUserUuid,
        imageKey,
      };

      const facilityResult = await createFacilityMutation.mutateAsync(facilityPayload);
      logger.info('Facility created successfully:', facilityResult.uuid);

      toast.success('Estabelecimento criado com sucesso!');

      // Notify parent component
      onFacilityCreated?.({
        uuid: facilityResult.uuid,
        name: data.name,
        cnpj: data.cnpj,
        city: data.city,
        state: data.state,
        cityIbgeCode: data.cityIbgeCode,
      });

      onOpenChange(false);
    } catch (error) {
      const message = (error as Error).message || 'Erro ao criar estabelecimento';
      toast.error(message);
      logger.error('Error creating facility:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canCreateFacility) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[80vh] w-[calc(100%-2rem)] overflow-y-auto sm:max-h-[90vh] sm:w-[500px] sm:max-w-[500px]"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#525252 transparent',
          // Force max-width on desktop to override base component styles
          maxWidth: 'min(500px, calc(100% - 2rem))',
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-primary flex items-center gap-2">
            <Icon name="add" size={20} />
            Criar novo estabelecimento
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
            {/* Facility Name */}
            <FormField
              control={form.control}
              name="name"
              rules={{ validate: (value) => checkFacilityCreateInput('name', value) }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                    Nome do estabelecimento
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Digite o nome do estabelecimento" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* CNPJ */}
            <FormField
              control={form.control}
              name="cnpj"
              rules={{ validate: (value) => checkFacilityCreateInput('cnpj', value) }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">CNPJ</FormLabel>
                  <FormControl>
                    <Input mask="99.999.999/9999-99" placeholder="00.000.000/0000-00" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* CEP and Address */}
            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="cep"
                rules={{ validate: (value) => checkFacilityCreateInput('cep', value) }}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">CEP</FormLabel>
                    <FormControl>
                      <Input mask="99999-999" placeholder="00000-000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                rules={{ validate: (value) => checkFacilityCreateInput('address', value) }}
                render={({ field }) => (
                  <FormItem className="flex-[2]">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Logradouro</FormLabel>
                    <FormControl>
                      <Input placeholder="Logradouro" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* District and Number */}
            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="district"
                rules={{ validate: (value) => checkFacilityCreateInput('district', value) }}
                render={({ field }) => (
                  <FormItem className="flex-[2]">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Bairro</FormLabel>
                    <FormControl>
                      <Input placeholder="Bairro" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="number"
                rules={{ validate: (value) => checkFacilityCreateInput('number', value) }}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Número</FormLabel>
                    <FormControl>
                      <Input placeholder="Nº" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* City and State */}
            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="city"
                rules={{ validate: (value) => checkFacilityCreateInput('city', value) }}
                render={({ field }) => (
                  <FormItem className="flex-[2]">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Cidade</FormLabel>
                    <FormControl>
                      <Input placeholder="Cidade" disabled {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="state"
                rules={{ validate: (value) => checkFacilityCreateInput('state', value) }}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">UF</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} disabled>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="UF" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {[
                          'AC',
                          'AL',
                          'AP',
                          'AM',
                          'BA',
                          'CE',
                          'DF',
                          'ES',
                          'GO',
                          'MA',
                          'MT',
                          'MS',
                          'MG',
                          'PA',
                          'PB',
                          'PR',
                          'PE',
                          'PI',
                          'RJ',
                          'RN',
                          'RS',
                          'RO',
                          'RR',
                          'SC',
                          'SP',
                          'SE',
                          'TO',
                        ].map((uf) => (
                          <SelectItem key={uf} value={uf}>
                            {uf}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Complement */}
            <FormField
              control={form.control}
              name="complement"
              rules={{ validate: (value) => checkFacilityCreateInput('complement', value) }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Complemento</FormLabel>
                  <FormControl>
                    <Input placeholder="Complemento (opcional)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Reference Location */}
            <FormField
              control={form.control}
              name="referenceLocation"
              rules={{ validate: (value) => checkFacilityCreateInput('referenceLocation', value) }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Ponto de Referência</FormLabel>
                  <FormControl>
                    <Input placeholder="Ponto de referência (opcional)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Responsible User Section */}
            <Card className="border-primary/30 bg-card/50">
              <CardContent className="pt-4">
                <div className="mb-4 flex items-center gap-2">
                  <Icon name="person" size={20} className="text-primary" />
                  <h3 className="text-primary font-semibold">Vincular responsável</h3>
                </div>
                <p className="text-muted-foreground mb-4 text-sm">
                  Selecione um promotor através do pesquisador abaixo, para vincular ao estabelecimento
                </p>

                {/* Search User */}
                <FormField
                  control={form.control}
                  name="responsibleUserObject"
                  rules={{
                    validate: () => {
                      if (isCreatingNewUser) return true;
                      const value = form.getValues('responsibleUser');
                      return checkFacilityCreateInput('responsibleUser', value);
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                        Pesquisar usuário
                      </FormLabel>
                      <FormControl>
                        <GenericSelect<UserDto>
                          onSelect={(user) => {
                            field.onChange(user);
                            form.setValue('responsibleUser', user.uuid);
                          }}
                          value={field.value}
                          disabled={isCreatingNewUser}
                          fetchItems={fetchUsers}
                          placeholder="Selecione ou digite para buscar"
                          listOnEmptySearch
                          renderItem={(user) => (
                            <div>
                              <div className="text-sm font-medium">{user.name}</div>
                              <div className="text-muted-foreground text-xs">{user.email}</div>
                            </div>
                          )}
                          getItemLabel={(user) => user.name}
                          getItemKey={(user) => user.uuid}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* OR divider */}
                {canCreateUser && (
                  <>
                    <div className="text-muted-foreground my-4 text-center text-sm">ou</div>

                    {/* Toggle New User Button - always visible */}
                    <Button
                      type="button"
                      variant={isCreatingNewUser ? 'ghost' : 'outline'}
                      className={
                        isCreatingNewUser
                          ? 'text-destructive hover:text-destructive/80 w-full'
                          : 'border-primary text-primary hover:bg-primary/10 w-full'
                      }
                      onClick={handleToggleNewUser}
                    >
                      <Icon name={isCreatingNewUser ? 'close' : 'add'} size={18} className="mr-2" />
                      {isCreatingNewUser ? 'Cancelar novo usuário' : 'Adicionar novo promotor'}
                    </Button>

                    {/* New User Fields - with smooth height transition */}
                    <div
                      className="grid transition-all duration-300 ease-in-out"
                      style={{
                        gridTemplateRows: isCreatingNewUser ? '1fr' : '0fr',
                      }}
                    >
                      <div className="overflow-hidden">
                        <div className="flex flex-col gap-4 pt-4">
                          {/* New User Fields */}
                          <FormField
                            control={form.control}
                            name="newUserName"
                            rules={{
                              validate: (value) => {
                                if (!isCreatingNewUser) return true;
                                if (!value || value.trim().length === 0) return 'Campo obrigatório';
                                // Validate fullname format: at least 2 words with 2+ characters each
                                const isValidFullname = /^[\p{L}]{2,}(?:\s[\p{L}]{2,})+\s*$/u.test(value);
                                if (!isValidFullname) return 'Informe nome e sobrenome';
                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                  Nome e sobrenome
                                </FormLabel>
                                <FormControl>
                                  <Input placeholder="Nome completo" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="newUserRegistration"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                  Matrícula
                                </FormLabel>
                                <FormControl>
                                  <Input placeholder="Matrícula (opcional)" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="newUserPhone"
                            rules={{
                              validate: (value) => {
                                if (!isCreatingNewUser) return true;
                                if (!value) return true; // Optional field
                                // Remove mask characters before validation
                                const cleanValue = value.replaceAll(/[^\d]/g, '');
                                if (cleanValue.length < 10) return 'O formato é inválido';
                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                  Número de Whatsapp
                                </FormLabel>
                                <FormControl>
                                  <Input mask="(99) 99999-9999" placeholder="(00) 00000-0000" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="newUserEmail"
                            rules={{
                              validate: (value) => {
                                if (!isCreatingNewUser) return true;
                                if (!value) return true; // Optional field
                                // Simple email format validation
                                const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
                                if (!isValidEmail) return 'O formato é inválido';
                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">E-mail</FormLabel>
                                <FormControl>
                                  <Input type="email" placeholder="email@exemplo.com" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Supervisor Field - outside overflow-hidden to allow dropdown to display properly */}
                    {isCreatingNewUser && (
                      <FormField
                        control={form.control}
                        name="newUserSupervisor"
                        rules={{
                          validate: () => {
                            if (!isCreatingNewUser) return true;
                            const value = form.getValues('newUserSupervisor');
                            if (!value) return 'Responsável direto é obrigatório';
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <FormItem className="mt-4">
                            <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                              Responsável direto
                            </FormLabel>
                            <FormControl>
                              <GenericSelect<UserDto>
                                onSelect={(user) => field.onChange(user)}
                                value={field.value}
                                placeholder="Selecione o responsável"
                                listOnEmptySearch
                                dependencies={[supervisorsList]}
                                fetchItems={async (search) => {
                                  if (!search) return supervisorsList;
                                  const searchLower = search.toLowerCase();
                                  return supervisorsList.filter(
                                    (u) =>
                                      u.name.toLowerCase().includes(searchLower) ||
                                      u.email?.toLowerCase().includes(searchLower)
                                  );
                                }}
                                renderItem={(user) => (
                                  <div>
                                    <div className="text-sm font-medium">{user.name}</div>
                                    <div className="text-muted-foreground text-xs">{user.email}</div>
                                  </div>
                                )}
                                getItemLabel={(user) => user.name}
                                getItemKey={(user) => user.uuid}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Button
              type="submit"
              className="bg-primary hover:bg-primary/90 w-full"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              <Icon name="save" size={18} className="mr-2" />
              Finalizar criação
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
